import React, { useState, useEffect } from 'react';
import {
  Send,
  Users,
  Mail,
  MessageSquare,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  X,
  Plus,
  FileText
} from 'lucide-react';
import { schoolDataApi } from '../../services/api';
import { sortClasses } from '../../utils/classUtils';

interface NotificationData {
  recipients: string[];
  subject: string;
  message: string;
  channels: string[];
  scheduleDate?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  templateId?: string;
}

const SendNotification: React.FC = () => {
  const [formData, setFormData] = useState<NotificationData>({
    recipients: [],
    subject: '',
    message: '',
    channels: ['email'],
    priority: 'medium'
  });

  const [classes, setClasses] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [recipientType, setRecipientType] = useState<'all' | 'class' | 'individual' | 'teachers'>('all');
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [selectedIndividuals, setSelectedIndividuals] = useState<string[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [classesData, studentsData, teachersData] = await Promise.all([
        schoolDataApi.classes.getAll(),
        schoolDataApi.students.getAll(),
        schoolDataApi.teachers.getAll()
      ]);

      setClasses(sortClasses(classesData.filter(cls => cls.isActive)));
      setStudents(studentsData);
      setTeachers(teachersData.filter(teacher => teacher.status === 'Active'));

      // Load templates (would be implemented in API)
      setTemplates([
        { id: '1', name: 'Exam Schedule', subject: 'Upcoming Examination Schedule', message: 'Dear Parents/Students,\n\nWe would like to inform you about the upcoming examination schedule...' },
        { id: '2', name: 'Holiday Notice', subject: 'School Holiday Announcement', message: 'Dear Parents/Students,\n\nThis is to inform you that the school will remain closed on...' },
        { id: '3', name: 'Fee Reminder', subject: 'Fee Payment Reminder', message: 'Dear Parents,\n\nThis is a gentle reminder that the school fee payment is due...' },
        { id: '4', name: 'Event Invitation', subject: 'School Event Invitation', message: 'Dear Parents/Students,\n\nWe are pleased to invite you to our upcoming school event...' }
      ]);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    try {
      setSending(true);

      // Calculate recipients based on selection
      let recipients: string[] = [];

      switch (recipientType) {
        case 'all':
          recipients = [...students.map(s => s.parentEmail), ...teachers.map(t => t.email)];
          break;
        case 'class':
          const classStudents = students.filter(s => selectedClasses.includes(s.class));
          recipients = classStudents.map(s => s.parentEmail);
          break;
        case 'individual':
          recipients = selectedIndividuals;
          break;
        case 'teachers':
          recipients = teachers.map(t => t.email);
          break;
      }

      const notificationData = {
        ...formData,
        recipients: recipients.filter(email => email), // Remove empty emails
        sentAt: new Date().toISOString(),
        sentBy: 'admin', // Would get from auth context
        totalRecipients: recipients.length
      };

      // Send notification (would be implemented in API)
      console.log('Sending notification:', notificationData);

      // Reset form
      setFormData({
        recipients: [],
        subject: '',
        message: '',
        channels: ['email'],
        priority: 'medium'
      });
      setRecipientType('all');
      setSelectedClasses([]);
      setSelectedIndividuals([]);

      alert('Notification sent successfully!');

    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Error sending notification. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleTemplateSelect = (template: any) => {
    setFormData(prev => ({
      ...prev,
      subject: template.subject,
      message: template.message,
      templateId: template.id
    }));
  };

  const handleClassToggle = (className: string) => {
    setSelectedClasses(prev =>
      prev.includes(className)
        ? prev.filter(c => c !== className)
        : [...prev, className]
    );
  };

  const handleIndividualToggle = (email: string) => {
    setSelectedIndividuals(prev =>
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const getRecipientCount = () => {
    switch (recipientType) {
      case 'all':
        return students.length + teachers.length;
      case 'class':
        return students.filter(s => selectedClasses.includes(s.class)).length;
      case 'individual':
        return selectedIndividuals.length;
      case 'teachers':
        return teachers.length;
      default:
        return 0;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Templates */}
      <div className="bg-[#28221a] border border-[#3a3122] rounded-lg p-4">
        <h3 className="text-sm font-medium text-[#f3ede2] mb-3 flex items-center">
          <FileText className="w-4 h-4 mr-2" />
          Quick Templates
        </h3>
        <div className="flex flex-wrap gap-2">
          {templates.map((template) => (
            <button
              key={template.id}
              onClick={() => handleTemplateSelect(template)}
              className="px-3 py-1.5 text-sm bg-[#211c15] border border-[#3a3122] rounded-lg hover:bg-[#e2916d] hover:text-white transition-colors"
            >
              {template.name}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recipients Selection */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-6">
            <h3 className="text-lg font-semibold text-[#f3ede2] mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2 text-[#e2916d]" />
              Recipients ({getRecipientCount()})
            </h3>

            <div className="space-y-4">
              {/* Recipient Type */}
              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Send To
                </label>
                <select
                  value={recipientType}
                  onChange={(e) => setRecipientType(e.target.value as any)}
                  className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                >
                  <option value="all">All Students & Teachers</option>
                  <option value="class">Specific Classes</option>
                  <option value="individual">Individual Recipients</option>
                  <option value="teachers">All Teachers</option>
                </select>
              </div>

              {/* Class Selection */}
              {recipientType === 'class' && (
                <div>
                  <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                    Select Classes
                  </label>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {classes.map((cls) => (
                      <label key={cls.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedClasses.includes(cls.name)}
                          onChange={() => handleClassToggle(cls.name)}
                          className="w-4 h-4 text-[#e2916d] focus:ring-[#e2916d] border-[#4a4030] rounded"
                        />
                        <span className="text-sm text-[#f3ede2]">{cls.name}</span>
                        <span className="text-xs text-[#8a7f6e]">
                          ({students.filter(s => s.class === cls.name).length} students)
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Individual Selection */}
              {recipientType === 'individual' && (
                <div>
                  <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                    Select Recipients
                  </label>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    <div className="text-xs font-medium text-[#b9ad9b] mb-2">Students (Parents)</div>
                    {students.map((student) => (
                      <label key={student.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedIndividuals.includes(student.parentEmail)}
                          onChange={() => handleIndividualToggle(student.parentEmail)}
                          className="w-4 h-4 text-[#e2916d] focus:ring-[#e2916d] border-[#4a4030] rounded"
                        />
                        <span className="text-sm text-[#f3ede2]">{student.name}</span>
                        <span className="text-xs text-[#8a7f6e]">({student.parentEmail})</span>
                      </label>
                    ))}
                    <div className="text-xs font-medium text-[#b9ad9b] mt-4 mb-2">Teachers</div>
                    {teachers.map((teacher) => (
                      <label key={teacher.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedIndividuals.includes(teacher.email)}
                          onChange={() => handleIndividualToggle(teacher.email)}
                          className="w-4 h-4 text-[#e2916d] focus:ring-[#e2916d] border-[#4a4030] rounded"
                        />
                        <span className="text-sm text-[#f3ede2]">{teacher.name}</span>
                        <span className="text-xs text-[#8a7f6e]">({teacher.email})</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notification Settings */}
          <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-6">
            <h3 className="text-lg font-semibold text-[#f3ede2] mb-4">Settings</h3>

            <div className="space-y-4">
              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              {/* Channels */}
              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Notification Channels
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.channels.includes('email')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, channels: [...prev.channels, 'email'] }));
                        } else {
                          setFormData(prev => ({ ...prev, channels: prev.channels.filter(c => c !== 'email') }));
                        }
                      }}
                      className="w-4 h-4 text-[#e2916d] focus:ring-[#e2916d] border-[#4a4030] rounded"
                    />
                    <Mail className="w-4 h-4 text-[#8a7f6e]" />
                    <span className="text-sm text-[#f3ede2]">Email</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.channels.includes('sms')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, channels: [...prev.channels, 'sms'] }));
                        } else {
                          setFormData(prev => ({ ...prev, channels: prev.channels.filter(c => c !== 'sms') }));
                        }
                      }}
                      className="w-4 h-4 text-[#e2916d] focus:ring-[#e2916d] border-[#4a4030] rounded"
                    />
                    <MessageSquare className="w-4 h-4 text-[#8a7f6e]" />
                    <span className="text-sm text-[#f3ede2]">SMS</span>
                  </label>
                </div>
              </div>

              {/* Schedule */}
              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Schedule (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.scheduleDate || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, scheduleDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                />
                <p className="text-xs text-[#8a7f6e] mt-1">Leave empty to send immediately</p>
              </div>
            </div>
          </div>
        </div>

        {/* Message Composition */}
        <div className="lg:col-span-2">
          <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-6">
            <h3 className="text-lg font-semibold text-[#f3ede2] mb-4 flex items-center">
              <Send className="w-5 h-5 mr-2 text-[#e2916d]" />
              Compose Message
            </h3>

            <div className="space-y-4">
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                  placeholder="Enter notification subject"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Message *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  rows={12}
                  className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                  placeholder="Enter your message here..."
                  required
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-[#8a7f6e]">
                    Characters: {formData.message.length}
                  </p>
                  <div className="flex items-center space-x-2 text-xs text-[#8a7f6e]">
                    <Clock className="w-3 h-3" />
                    <span>Est. reading time: {Math.max(1, Math.ceil(formData.message.split(' ').length / 200))} min</span>
                  </div>
                </div>
              </div>

              {/* Preview */}
              {formData.subject && formData.message && (
                <div className="bg-[#28221a] border border-[#3a3122] rounded-lg p-4">
                  <h4 className="text-sm font-medium text-[#f3ede2] mb-2">Preview</h4>
                  <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-4">
                    <div className="text-sm font-medium text-[#f3ede2] mb-2">{formData.subject}</div>
                    <div className="text-sm text-[#b9ad9b] whitespace-pre-wrap">{formData.message}</div>
                  </div>
                </div>
              )}

              {/* Send Button */}
              <div className="flex items-center justify-between pt-4 border-t border-[#3a3122]">
                <div className="flex items-center space-x-2 text-sm text-[#8a7f6e]">
                  <AlertCircle className="w-4 h-4" />
                  <span>This will be sent to {getRecipientCount()} recipients</span>
                </div>
                <button
                  onClick={handleSendNotification}
                  disabled={sending || !formData.subject || !formData.message || getRecipientCount() === 0}
                  className="px-6 py-2 bg-[#e2916d] hover:bg-[#f0a67d] disabled:bg-[#8a7f6e] text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  {sending && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  <Send className="w-4 h-4" />
                  <span>{sending ? 'Sending...' : formData.scheduleDate ? 'Schedule' : 'Send Now'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendNotification;